/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import CompanyBasicFormMainContent from './CompanyBasicFormComponents/CompanyBasicFormMainContent'
import CompanyBasicDuplicateWarning from './CompanyBasicFormComponents/CompanyBasicDuplicateWarning'
import { CompanyFormProps } from 'types/entities/specific/CompanyFormTypes'
import { CompanyBranchType } from 'types/enums/CompanyBranchType'
import { useCompanyBasicForm } from 'utils/hooks/useCompanyBasicForm'

export interface CompanyBasicFormHandle {
    save: () => Promise<void>
}

const CompanyBasicForm = forwardRef<CompanyBasicFormHandle, CompanyFormProps>(
    ({ onChange, compact = false, showSaveAndClose = false, isInEditModal = false, onDuplicateClick }, ref) => {
        const { t } = useTranslation()

        const {
            form,
            isSubmitting,
            showPopover,
            handleMouseEnter,
            handleMouseLeave,
            parentCompany,
            setParentCompany,
            duplicates,
            isOpeningMergeModal,
            notesFontSize,
            setNotesFontSize,
            employeeSizeField,
            businessCategoryField,
            branchTypeField,
            businessCategoryOptionsUi,
            businessBranchTypeOptionsUi,
            employeeSizeOptionsUi,
            handleOpenMergeModal,
            onSubmit,
            onError,
            saveAndCloseText,
        } = useCompanyBasicForm({
            onChange,
            isInEditModal,
            showSaveAndClose,
        })

        const {
            register,
            control,
            handleSubmit,
            setValue,
            formState: { errors },
        } = form

        useImperativeHandle(
            ref,
            () => ({
                save: async () => {
                    return new Promise<void>((resolve) => {
                        handleSubmit(async (data) => {
                            await onSubmit(data, false)
                            resolve()
                        }, onError)()
                    })
                },
            }),
            [handleSubmit, onError, onSubmit]
        )

        const formClassName = compact ? '[&_input]:text-xs! [&_select]:text-xs! [&_textarea]:text-xs!' : ''

        return (
            <form
                onSubmit={handleSubmit((data) => onSubmit(data, true), onError)}
                autoComplete="off"
                className={clsx('CompanyBasicForm flex flex-col grow relative', formClassName)}
            >
                {!isInEditModal && (
                    <CompanyBasicDuplicateWarning
                        duplicates={duplicates}
                        isOpeningMergeModal={isOpeningMergeModal}
                        showDuplicatesPopover={showPopover}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleOpenMergeModal}
                        onDuplicateClick={onDuplicateClick}
                        floating={true}
                    />
                )}

                <CompanyBasicFormMainContent
                    compact={compact}
                    register={register}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    duplicates={duplicates}
                    isInEditModal={isInEditModal}
                    isOpeningMergeModal={isOpeningMergeModal}
                    showDuplicatesPopover={showPopover}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                    handleOpenMergeModal={handleOpenMergeModal}
                    onDuplicateClick={onDuplicateClick}
                    selectedBusinessBranchType={branchTypeField.value as CompanyBranchType}
                    parentCompany={parentCompany}
                    setParentCompany={setParentCompany}
                    businessCategoryOptionsUi={businessCategoryOptionsUi}
                    selectedBusinessCategory={businessCategoryField.value}
                    setSelectedBusinessCategory={businessCategoryField.setValue}
                    businessBranchTypeOptionsUi={businessBranchTypeOptionsUi}
                    setSelectedBusinessBranchType={branchTypeField.setValue}
                    employeeSizeOptionsUi={employeeSizeOptionsUi}
                    selectedEmployeeSize={employeeSizeField.value}
                    setSelectedEmployeeSize={employeeSizeField.setValue}
                    notesFontSize={notesFontSize}
                    setNotesFontSize={setNotesFontSize}
                />

                <UiFormActionsBar
                    primaryId={'btn-companies-wizard-basic-save'}
                    primaryText={isSubmitting ? t('Saving company...') : t('Save')}
                    onPrimary={handleSubmit((data) => onSubmit(data, false), onError)}
                    saveAndCloseText={saveAndCloseText}
                    onSaveAndClose={showSaveAndClose ? handleSubmit((data) => onSubmit(data, true), onError) : undefined}
                    loading={isSubmitting}
                />
            </form>
        )
    }
)

CompanyBasicForm.displayName = 'CompanyBasicForm'

export default CompanyBasicForm